// Maps a Recurly account's existing subscriptions (parent + child) into
// proposalInfo option `items`, using productConfigInfo to classify each
// plan as a center/app/one-time fee and to look up its uigroup.

const TYPE_BY_PROD_TYPE = {
  Center: 'CENTER',
  App: 'APP',
  OneTimeFees: 'ONE_TIME_FEE',
}

function findProductConfig(productConfigInfo, planCode) {
  const {centers = [], apps = [], oneTimeFees = []} = productConfigInfo ?? {}

  return (
    centers.find(product => product.code === planCode) ??
    apps.find(product => product.code === planCode) ??
    oneTimeFees.find(product => product.code === planCode) ??
    null
  )
}

// For tiered add-ons, unitAmount is null and the price lives in the tiers array.
function resolveAddonUnitAmount(subscriptionAddOn) {
  if (subscriptionAddOn.unitAmount != null)
    return Number(subscriptionAddOn.unitAmount)

  const tiers = subscriptionAddOn.tiers ?? []
  const {tierType, quantity = 1} = subscriptionAddOn
  if (!tiers.length) return 0

  const sortedAsc = [...tiers].sort(
    (a, b) => (a.endingQuantity ?? Infinity) - (b.endingQuantity ?? Infinity),
  )

  let tier
  if (tierType === 'volume') {
    tier = sortedAsc.find(
      t => t.endingQuantity == null || quantity <= t.endingQuantity,
    )
  } else {
    tier =
      sortedAsc.find(
        t => t.endingQuantity == null || t.endingQuantity >= quantity,
      ) ?? sortedAsc[sortedAsc.length - 1]
  }

  if (!tier) return 0
  if (tier.unitAmount != null) return Number(tier.unitAmount)
  return Number(tier.currencies?.[0]?.unitAmount ?? 0)
}

function isStairstepTiered(subscriptionAddOn) {
  return (
    subscriptionAddOn.unitAmount == null &&
    (subscriptionAddOn.tiers?.length ?? 0) > 0 &&
    subscriptionAddOn.tierType !== 'volume'
  )
}

function mapAddOns(addOns = []) {
  return addOns.map(subscriptionAddOn => {
    const isSS = isStairstepTiered(subscriptionAddOn)
    const qty = isSS ? 1 : subscriptionAddOn.quantity ?? 1
    return {
      productCode: subscriptionAddOn.addOn?.code ?? null,
      productName: subscriptionAddOn.addOn?.name ?? null,
      productType: 'APP',
      quantity: qty,
      // unitPrice must be the total (perUnit × qty), not per-unit — mirrors
      // subscriptionToItem's `unitAmount * quantity`.
      unitPrice: resolveAddonUnitAmount(subscriptionAddOn) * qty,
      alreadyIncluded: true,
    }
  })
}

function mapCustomFields(customFields = []) {
  return customFields.map(field => ({name: field.name, value: field.value}))
}

function subscriptionToItem(subscription, productConfigInfo) {
  const planCode = subscription.plan?.code
  if (!planCode) return null

  const productConfig = findProductConfig(productConfigInfo, planCode)

  const quantity = subscription.quantity ?? 1
  const unitAmount = Number(subscription.unitAmount ?? 0)

  return {
    productCode: planCode,
    productName: subscription.plan?.name ?? productConfig?.name ?? null,
    unitPrice: unitAmount * quantity,
    // recurlyQuantity preserves the raw Recurly subscription quantity for the
    // cc/order/preview-payment payload (via resolveQuantity) without setting
    // item.quantity — display code that does unitPrice * quantity would
    // double-count for this plan where quantity encodes the dollar amount
    // (unitAmount=1, quantity=$amount).
    ...(planCode === 'seobase-5-thryv-bni-nt-m2m' && {
      recurlyQuantity: quantity,
    }),
    productType: TYPE_BY_PROD_TYPE[productConfig?.prod_type] ?? 'CENTER',
    billingFrequency:
      productConfig?.productBillingType === 'Annual' ? 'annual' : 'monthly',
    configuration: mapCustomFields(subscription.customFields),
    addons: mapAddOns(subscription.addOns),
    alreadyIncluded: true,
  }
}

// Legacy BNI marketing-services subscriptions hidden from the account's
// existing-item list when Enable_BNI_Mktg_Svcs is off — mirrors
// screens/catalog.js:1680-1690.
const BNI_MKTG_SVCS_CODES = [
  'addon-5-mktgsvcs-ypcom-m2m',
  'addon-5-mktgsvcs-ypcom-annual',
  'addon-5-mktgsvcs-soci-m2m',
  'addon-5-mktgsvcs-soci-annual',
]

// AEO & SEO's addons (PowerBoost, Keywords) are genuine nested quantity-
// stepper line items — see PLAN_ADDONS in ProductPlanSelector/panels/
// SeoAddonPanel.js — and must stay under the SEO plan rather than being
// promoted to top-level siblings, even though SEO is classified APP.
const SEO_PRODUCT_CODE = 'seobase-5-thryv-m2m'

const STANDALONE_ADDON_CODES = new Set([
  'addon-socialadsbronze-5-m2m',
  'addon-socialadssilver-5-m2m',
  'addon-socialadsgold-5-m2m',
  'addon-5-mcexpangr',
  'addon-5-mcstartgr',
])

// Only APP items (e.g. Keap Add-Ons wrapper) can have real sub-addons worth
// promoting. CENTER items carry entitlements in their addons array — those
// must stay nested so rules like hasWebsiteEntitlement keep working.
//
// Shape-agnostic: operates purely on the common mapped-item shape
// ({productCode, productType, billingFrequency, addons: [...]}), so it's
// shared by every subscription-source mapper regardless of the raw shape
// that source's API returns (see buildItemsFromRecurly and
// buildItemsFromAccountSubscriptions below).
export function flattenStandaloneAddonItems(items) {
  return items.flatMap(({addons, ...item}) => {
    const standaloneAddons = addons.filter(addon =>
      STANDALONE_ADDON_CODES.has(addon.productCode),
    )
    if (!standaloneAddons.length) {
      if (
        item.productType !== 'APP' ||
        item.productCode === SEO_PRODUCT_CODE ||
        !addons.length
      ) {
        return [addons.length ? {...item, addons} : item]
      }
      return [
        item,
        ...addons.map(addon => ({
          ...addon,
          billingFrequency: item.billingFrequency,
          alreadyIncluded: true,
        })),
      ]
    }

    // New behavior
    const remainingAddons = addons.filter(
      addon => !STANDALONE_ADDON_CODES.has(addon.productCode),
    )

    return [
      {
        ...item,
        ...(remainingAddons.length && {addons: remainingAddons}),
      },
      ...standaloneAddons.map(addon => ({
        ...addon,
        billingFrequency: item.billingFrequency,
        alreadyIncluded: true,
      })),
    ]
  })
}

export function buildItemsFromRecurly(
  recurlyAccountInfo,
  productConfigInfo,
  // Defaults false (strip) — mirrors catalog.js:1680's `if (!Enable_BNI_Mktg_Svcs)`,
  // where an unloaded/absent flag is falsy too.
  {isBniMktgSvcsEnabled = false} = {},
) {
  const payload = recurlyAccountInfo?.payload ?? recurlyAccountInfo ?? {}

  const subscriptions = [
    ...(payload.parentSubscriptions ?? []),
    ...(payload.childSubscriptions ?? []),
  ].filter(
    subscription =>
      isBniMktgSvcsEnabled ||
      !BNI_MKTG_SVCS_CODES.includes(subscription.plan?.code),
  )

  const items = subscriptions
    .filter(subscription => subscription.state === 'active')
    .map(subscription => subscriptionToItem(subscription, productConfigInfo))
    .filter(Boolean)

  return flattenStandaloneAddonItems(items)
}

// ---------------------------------------------------------------------------
// accounts/5/subscriptions (GET accounts/5/subscriptions?thryvId=...) shape
// ---------------------------------------------------------------------------
// This endpoint's account-service _mapSubscriptions already flattens the raw
// Recurly SDK shape server-side: subscription.plan.code/name become
// subscription.code/name, subscription.addOns[].addOn.code/name become
// subscription.addons[].code/name (note lowercase key, singular addon per
// entry), and tiers/tierType are resolved server-side and dropped — addon
// unitAmount/subtotal already reflect the resolved (tier-aware) total, so no
// client-side tier math is needed here, unlike subscriptionToItem/mapAddOns
// above which operate on the raw, unresolved Recurly SDK shape.
function mapAccountSubscriptionAddOns(addons = []) {
  return addons.map(addon => {
    const quantity = addon.tierQuantity ?? addon.quantity ?? 1
    return {
      productCode: addon.code ?? null,
      productName: addon.name ?? null,
      productType: 'APP',
      quantity,
      unitPrice: Number(
        addon.subtotal ?? Number(addon.unitAmount ?? 0) * quantity,
      ),
      alreadyIncluded: true,
    }
  })
}

function accountSubscriptionToItem(subscription, productConfigInfo) {
  const planCode = subscription.code
  if (!planCode) return null

  const productConfig = findProductConfig(productConfigInfo, planCode)
  const quantity = subscription.quantity ?? 1
  const addons = mapAccountSubscriptionAddOns(subscription.addons)

  // `subscription.subtotal` is the whole Recurly subscription, add-ons
  // included. flattenStandaloneAddonItems later promotes the standalone ones
  // (e.g. Starter/Expanded Growth Package) to their own items, so leave their
  // price out of the parent — otherwise it is counted twice, and removing the
  // add-on from the cart (Starter -> Expanded swap) still leaves its price on
  // the parent in "Due Monthly".
  const promotedAddonsPrice =
    subscription.subtotal == null
      ? 0
      : addons
          .filter(addon => STANDALONE_ADDON_CODES.has(addon.productCode))
          .reduce((sum, addon) => sum + addon.unitPrice, 0)

  return {
    productCode: planCode,
    productName: subscription.name ?? productConfig?.name ?? null,
    unitPrice: Math.max(
      0,
      Number(
        subscription.subtotal ??
          Number(subscription.unitAmount ?? 0) * quantity,
      ) - promotedAddonsPrice,
    ),
    // Preserve the raw Recurly subscription quantity so that downstream
    // payloads (buildSendLinkSubscriptionGroups → mapItemsToCentersApps,
    // buildCreateOrderPayload) write the correct value into order_line.quantity
    // for plans like seobase-5-thryv-m2m and tl-tax-5-thryv-m2m where Recurly
    // stores the dollar amount as the quantity (unitAmount=1, quantity=$amount).
    // Without this, order_line.quantity defaults to 1 for all existing
    // subscriptions, causing the payment page to display wrong prices on upgrade
    // orders. unitPrice above already captures the total charge, so display code
    // that uses unitPrice directly (rather than unitPrice × quantity) is
    // unaffected.
    quantity,
    ...(planCode === 'seobase-5-thryv-bni-nt-m2m' && {
      recurlyQuantity: quantity,
    }),
    productType: TYPE_BY_PROD_TYPE[productConfig?.prod_type] ?? 'CENTER',
    billingFrequency:
      productConfig?.productBillingType === 'Annual' ? 'annual' : 'monthly',
    configuration: mapCustomFields(subscription.customFields),
    addons,
    alreadyIncluded: true,
  }
}

// Analog of buildItemsFromRecurly, for the accounts/5/subscriptions response
// shape (used by the Create Purchase Link flow's per-location fetch) rather
// than the raw Recurly-SDK shape orders/validate-email returns (used by the
// Proposal Tool). The two are NOT interchangeable — see the field-shape notes
// above — so accountSubscriptions here must come from accounts/5/subscriptions,
// never from orders/validate-email's subDetails.
export function buildItemsFromAccountSubscriptions(
  accountSubscriptions,
  productConfigInfo,
  {isBniMktgSvcsEnabled = false} = {},
) {
  const payload = accountSubscriptions ?? {}

  const subscriptions = [
    ...(payload.parentSubscriptions ?? []),
    ...(payload.childSubscriptions ?? []),
  ].filter(
    subscription =>
      isBniMktgSvcsEnabled || !BNI_MKTG_SVCS_CODES.includes(subscription.code),
  )

  const items = subscriptions
    .filter(subscription => subscription.state === 'active')
    .map(subscription =>
      accountSubscriptionToItem(subscription, productConfigInfo),
    )
    .filter(Boolean)

  return flattenStandaloneAddonItems(items)
}
